export type Period = 'day' | 'week' | 'month';
export type Mode = 'rolling' | 'todate';

export type Range = { since: string; until: string };
export type PeriodRanges = {
  current: Range;
  previous: Range;
  currentLabel: string;
  previousLabel: string;
};

const ISO = (d: Date) => d.toISOString().slice(0, 10);

function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day));
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
}

function startOfWeekMonday(d: Date): Date {
  const day = d.getUTCDay();
  const offset = (day + 6) % 7;
  return addDays(d, -offset);
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function shiftMonth(d: Date, months: number): Date {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + months;
  const day = Math.min(d.getUTCDate(), daysInMonth(y + Math.floor((m) / 12), ((m % 12) + 12) % 12));
  return utcDate(y, m, day);
}

const MONTH_NAMES_PT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

function fmtRange(since: string, until: string): string {
  if (since === until) return fmtDay(since);
  return `${fmtDay(since)} – ${fmtDay(until)}`;
}

function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTH_NAMES_PT[(m || 1) - 1]}`;
}

/**
 * Produces a current+previous date range for the given period and comparison mode.
 *
 * Reference: "today" in UTC. Meta Ads insights are day-bucketed, so the smallest
 * unit is a full day.
 *
 * - day/rolling   : current = yesterday (full day)        vs previous = day before yesterday
 * - day/todate    : current = today (so far)              vs previous = yesterday (full day)
 * - week/rolling  : current = last 7 days (incl. today)   vs previous = the 7 days before that
 * - week/todate   : current = Monday→today                vs previous = previous Mon→same weekday
 * - month/rolling : current = last 30 days (incl. today)  vs previous = the 30 days before that
 * - month/todate  : current = day 1 of this month→today   vs previous = day 1 of last month→same day-of-month
 */
export function periodRanges(period: Period, mode: Mode, now: Date = new Date()): PeriodRanges {
  const today = utcDate(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  if (period === 'day') {
    if (mode === 'rolling') {
      const cur = addDays(today, -1);
      const prev = addDays(today, -2);
      return {
        current: { since: ISO(cur), until: ISO(cur) },
        previous: { since: ISO(prev), until: ISO(prev) },
        currentLabel: 'Ontem',
        previousLabel: 'Anteontem',
      };
    }
    const prev = addDays(today, -1);
    return {
      current: { since: ISO(today), until: ISO(today) },
      previous: { since: ISO(prev), until: ISO(prev) },
      currentLabel: 'Hoje',
      previousLabel: 'Ontem',
    };
  }

  if (period === 'week') {
    if (mode === 'rolling') {
      const curEnd = today;
      const curStart = addDays(today, -6);
      const prevEnd = addDays(curStart, -1);
      const prevStart = addDays(prevEnd, -6);
      return {
        current: { since: ISO(curStart), until: ISO(curEnd) },
        previous: { since: ISO(prevStart), until: ISO(prevEnd) },
        currentLabel: `Últimos 7 dias · ${fmtRange(ISO(curStart), ISO(curEnd))}`,
        previousLabel: `7 dias anteriores · ${fmtRange(ISO(prevStart), ISO(prevEnd))}`,
      };
    }
    const curStart = startOfWeekMonday(today);
    const curEnd = today;
    const prevStart = addDays(curStart, -7);
    const prevEnd = addDays(curEnd, -7);
    return {
      current: { since: ISO(curStart), until: ISO(curEnd) },
      previous: { since: ISO(prevStart), until: ISO(prevEnd) },
      currentLabel: `Esta semana · ${fmtRange(ISO(curStart), ISO(curEnd))}`,
      previousLabel: `Semana passada · ${fmtRange(ISO(prevStart), ISO(prevEnd))}`,
    };
  }

  // month
  if (mode === 'rolling') {
    const curEnd = today;
    const curStart = addDays(today, -29);
    const prevEnd = addDays(curStart, -1);
    const prevStart = addDays(prevEnd, -29);
    return {
      current: { since: ISO(curStart), until: ISO(curEnd) },
      previous: { since: ISO(prevStart), until: ISO(prevEnd) },
      currentLabel: `Últimos 30 dias · ${fmtRange(ISO(curStart), ISO(curEnd))}`,
      previousLabel: `30 dias anteriores · ${fmtRange(ISO(prevStart), ISO(prevEnd))}`,
    };
  }
  const curStart = utcDate(today.getUTCFullYear(), today.getUTCMonth(), 1);
  const curEnd = today;
  const prevStart = shiftMonth(curStart, -1);
  const prevEnd = shiftMonth(curEnd, -1);
  return {
    current: { since: ISO(curStart), until: ISO(curEnd) },
    previous: { since: ISO(prevStart), until: ISO(prevEnd) },
    currentLabel: `Este mês · ${fmtRange(ISO(curStart), ISO(curEnd))}`,
    previousLabel: `Mês passado · ${fmtRange(ISO(prevStart), ISO(prevEnd))}`,
  };
}

/**
 * Returns the widest range that covers both `current` and `previous`. We fetch the
 * widest range once and split it client-side to keep the cache key footprint small.
 */
export function unionRange(a: Range, b: Range): Range {
  return {
    since: a.since < b.since ? a.since : b.since,
    until: a.until > b.until ? a.until : b.until,
  };
}

export function inRange(date: string | undefined, r: Range): boolean {
  if (!date) return false;
  return date >= r.since && date <= r.until;
}
